export const STANDARDS_PAGES = [
  {
    slug: "standards/assurance-client",
    fr: {
      eyebrow: "Standards · Assurance client",
      title: "La confiance intégrée à chaque mandat",
      intro:
        "Comment nous protégeons la confiance, la continuité, la discrétion et la qualité de décision dans des relations de conseil complexes.",
      chips: ["Mandats senior", "Accès privé", "Équipe coordonnée unique"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Un standard de conseil plus contrôlé",
          items: [
            { title: "Conçu pour le travail à haute confiance", desc: "Nos mandats impliquent des décisions de capital sensibles et des questions de patrimoine familial : confidentialité, chemins d'escalade clairs et implication senior directe." },
            { title: "Communication structurée", desc: "Un rythme clair pour les mises à jour, décisions, documents et points ouverts — une base disciplinée pour agir." },
            { title: "Continuité entre les travaux", desc: "Stratégie, structuration, conformité, financement et mise en œuvre coordonnés comme un seul mandat." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Des éléments de confiance qui soutiennent la conversation",
          items: [
            { title: "Processus de découverte confidentiel", desc: "Vos informations ne circulent que dans un cadre contrôlé, dès le premier échange." },
            { title: "Gouvernance de mandat définie", desc: "Périmètre, jalons et responsabilités écrits avant le démarrage." },
            { title: "Documentation prête pour la décision", desc: "Des supports qui supportent l'examen d'un conseil d'administration." },
            { title: "Chemin d'escalade senior", desc: "Un associé joignable quand le jugement compte plus que le process." },
            { title: "Accès contrôlé aux documents", desc: "Règles d'accès claires pour chaque dossier et chaque interlocuteur." },
            { title: "Suivi de périmètre & jalons", desc: "Chaque engagement suit un plan mesurable, revu avec vous." },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Standards · Client assurance",
      title: "Confidence built into every mandate",
      intro:
        "How we protect trust, continuity, discretion and decision quality across complex advisory relationships.",
      chips: ["Senior-led mandates", "Private access", "One coordinated team"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "A more controlled advisory standard",
          items: [
            { title: "Designed for high-trust work", desc: "Our mandates involve sensitive capital decisions and family wealth matters: confidentiality, clear escalation paths and direct senior involvement." },
            { title: "Structured communication", desc: "A clear cadence for updates, decisions, documents and open issues — a disciplined basis for action." },
            { title: "Continuity across workstreams", desc: "Strategy, structuring, compliance, financing and implementation coordinated as one mandate." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Trust elements that support the conversation",
          items: [
            { title: "Confidential discovery process", desc: "Your information moves only within a controlled frame, from the first exchange." },
            { title: "Defined mandate governance", desc: "Scope, milestones and responsibilities in writing before work begins." },
            { title: "Decision-ready documentation", desc: "Materials that withstand board-level review." },
            { title: "Senior escalation path", desc: "A partner reachable when judgement matters more than process." },
            { title: "Controlled access to materials", desc: "Clear access rules for every file and every counterpart." },
            { title: "Scope & milestone tracking", desc: "Every engagement follows a measurable plan, reviewed with you." },
          ],
        },
      ],
    },
    de: {
      eyebrow: "Standards · Mandatsqualität",
      title: "Vertrauen, eingebaut in jedes Mandat",
      intro:
        "Wie wir Vertrauen, Kontinuität, Diskretion und Entscheidungsqualität in komplexen Beratungsbeziehungen schützen.",
      chips: ["Seniorgeführte Mandate", "Privater Zugang", "Ein koordiniertes Team"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Ein kontrollierterer Beratungsstandard",
          items: [
            { title: "Gestaltet für vertrauensintensive Arbeit", desc: "Unsere Mandate betreffen sensible Kapitalentscheidungen und Familienvermögen: Vertraulichkeit, klare Eskalationswege und direkte Senior-Beteiligung." },
            { title: "Strukturierte Kommunikation", desc: "Ein klarer Rhythmus für Updates, Entscheidungen, Dokumente und offene Punkte — eine disziplinierte Grundlage zum Handeln." },
            { title: "Kontinuität über Arbeitspakete", desc: "Strategie, Strukturierung, Compliance, Finanzierung und Umsetzung als ein Mandat koordiniert." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Vertrauenselemente, die das Gespräch tragen",
          items: [
            { title: "Vertraulicher Kennenlernprozess", desc: "Ihre Informationen bewegen sich nur in einem kontrollierten Rahmen, ab dem ersten Austausch." },
            { title: "Definierte Mandats-Governance", desc: "Umfang, Meilensteine und Verantwortlichkeiten schriftlich vor Arbeitsbeginn." },
            { title: "Entscheidungsreife Dokumentation", desc: "Unterlagen, die der Prüfung durch einen Verwaltungsrat standhalten." },
            { title: "Senior-Eskalationsweg", desc: "Ein Partner ist erreichbar, wenn Urteilsvermögen mehr zählt als Prozess." },
            { title: "Kontrollierter Dokumentenzugang", desc: "Klare Zugriffsregeln für jedes Dossier und jeden Gesprächspartner." },
            { title: "Umfangs- & Meilenstein-Tracking", desc: "Jedes Engagement folgt einem messbaren Plan, der mit Ihnen überprüft wird." },
          ],
        },
      ],
    },
  },
  {
    slug: "standards/modele-operationnel",
    fr: {
      eyebrow: "Standards · Modèle opérationnel",
      title: "Un modèle boutique, une discipline d'entreprise",
      intro:
        "Comment nous organisons le conseil senior, l'apport des spécialistes, le soutien à l'exécution et la communication client.",
      chips: ["01 Diagnostic", "02 Structure", "03 Exécution", "04 Pérennisation"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Un standard de conseil plus contrôlé",
          items: [
            { title: "Un mandat, plusieurs disciplines", desc: "Une même question peut toucher structure juridique, stratégie de capital, fiscalité et narratif investisseur. Nous gardons ces fils connectés." },
            { title: "Senior par conception", desc: "Les conseillers seniors restent proches des conversations sensibles et des décisions à fort enjeu." },
            { title: "Une documentation qui voyage", desc: "Des supports préparés pour les approbations internes, les investisseurs, les conseils et les contreparties." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "La mécanique d'un mandat",
          items: [
            { title: "Cadrage de mandat", desc: "Objectifs, contraintes et périmètre écrits et validés." },
            { title: "Responsabilité par flux", desc: "Un propriétaire identifié pour chaque workstream." },
            { title: "Cartographie des parties prenantes", desc: "Qui décide, qui conseille, qui signe — cartographié dès le départ." },
            { title: "Rythme de décision hebdomadaire", desc: "Des points courts et réguliers plutôt que des réunions interminables." },
            { title: "Coordination des conseils", desc: "Notaires, fiscalistes et banques orchestrés dans un seul calendrier." },
            { title: "Passation post-mandat", desc: "Transfert documenté pour que la structure vive sans nous." },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Standards · Operating model",
      title: "A boutique model with enterprise discipline",
      intro:
        "How we organise senior advisory work, specialist input, execution support and client communication.",
      chips: ["01 Diagnosis", "02 Structure", "03 Execution", "04 Sustain"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "A more controlled advisory standard",
          items: [
            { title: "One mandate, multiple disciplines", desc: "A single question can touch legal structure, capital strategy, tax and investor narrative. We keep those threads connected." },
            { title: "Senior-led by design", desc: "Senior advisors stay close to sensitive conversations and high-stakes decisions." },
            { title: "Documentation that travels", desc: "Materials prepared for internal approvals, investors, counsel and counterparties." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "The mechanics of a mandate",
          items: [
            { title: "Mandate scoping", desc: "Objectives, constraints and scope in writing and validated." },
            { title: "Workstream ownership", desc: "A named owner for every workstream." },
            { title: "Stakeholder mapping", desc: "Who decides, who advises, who signs — mapped from the start." },
            { title: "Weekly decision rhythm", desc: "Short, regular checkpoints rather than endless meetings." },
            { title: "Advisor coordination", desc: "Notaries, tax counsel and banks orchestrated in one timetable." },
            { title: "Post-engagement handover", desc: "Documented transfer so the structure lives on without us." },
          ],
        },
      ],
    },
    de: {
      eyebrow: "Standards · Betriebsmodell",
      title: "Ein Boutique-Modell mit Unternehmensdisziplin",
      intro:
        "Wie wir Senior-Beratung, Spezialistenbeiträge, Umsetzungsunterstützung und Kundenkommunikation organisieren.",
      chips: ["01 Diagnose", "02 Struktur", "03 Ausführung", "04 Nachhaltigkeit"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Ein kontrollierterer Beratungsstandard",
          items: [
            { title: "Ein Mandat, mehrere Disziplinen", desc: "Eine einzige Frage kann Rechtsstruktur, Kapitalstrategie, Steuern und Investorennarrativ berühren. Wir halten diese Fäden verbunden." },
            { title: "Von Konzept an seniorgeführt", desc: "Senior-Berater bleiben nah an sensiblen Gesprächen und Entscheidungen mit grosser Tragweite." },
            { title: "Dokumentation, die reist", desc: "Unterlagen, vorbereitet für interne Genehmigungen, Investoren, Berater und Gegenparteien." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Die Mechanik eines Mandats",
          items: [
            { title: "Mandatsabgrenzung", desc: "Ziele, Rahmenbedingungen und Umfang schriftlich festgehalten und validiert." },
            { title: "Verantwortung pro Workstream", desc: "Ein benannter Verantwortlicher für jedes Arbeitspaket." },
            { title: "Stakeholder-Mapping", desc: "Wer entscheidet, wer berät, wer unterschreibt — von Anfang an kartiert." },
            { title: "Wöchentlicher Entscheidungsrhythmus", desc: "Kurze, regelmässige Checkpoints statt endloser Sitzungen." },
            { title: "Beraterkoordination", desc: "Notare, Steuerberater und Banken in einem einzigen Zeitplan orchestriert." },
            { title: "Übergabe nach dem Mandat", desc: "Dokumentierte Übergabe, damit die Struktur ohne uns weiterlebt." },
          ],
        },
      ],
    },
  },
  {
    slug: "standards/gouvernance",
    fr: {
      eyebrow: "Standards · Gouvernance",
      title: "Des cadres de décision qui tiennent dans la durée",
      intro:
        "Conseils, comités, reporting et droits de décision : des cadres de gouvernance pour les clients qui opèrent entre investisseurs, entités et juridictions.",
      chips: ["Conseils & comités", "Droits de décision", "Reporting"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Ce que nous concevons",
          items: [
            { title: "Cadres de conseil", desc: "Composition, compétences et rythme des organes de décision, écrits et adoptés." },
            { title: "Droits de décision", desc: "Qui décide quoi, à quel seuil, avec quel quorum — sans zone grise." },
            { title: "Reporting d'oversight", desc: "Un reporting pensé pour l'action, pas pour l'archive." },
            { title: "Gouvernance familiale", desc: "Chartes, assemblées et règles d'entrée pour les patrimoines familiaux." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "« La bonne gouvernance est celle qui rend les conflits inutiles — parce que les règles existaient avant les désaccords. »",
        },
      ],
    },
    en: {
      eyebrow: "Standards · Governance",
      title: "Decision frameworks built to last",
      intro:
        "Boards, committees, reporting and decision rights: governance frameworks for clients operating across investors, entities and jurisdictions.",
      chips: ["Boards & committees", "Decision rights", "Reporting"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "What we design",
          items: [
            { title: "Board frameworks", desc: "Composition, competencies and rhythm of decision bodies, written and adopted." },
            { title: "Decision rights", desc: "Who decides what, at which threshold, with which quorum — no grey areas." },
            { title: "Oversight reporting", desc: "Reporting designed for action, not for the archive." },
            { title: "Family governance", desc: "Charters, assemblies and entry rules for family wealth." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "\u00AB Good governance makes conflict unnecessary — because the rules existed before the disagreements. \u00BB",
        },
      ],
    },
    de: {
      eyebrow: "Standards · Governance",
      title: "Entscheidungsrahmen, gebaut für die Dauer",
      intro:
        "Gremien, Ausschüsse, Reporting und Entscheidungsrechte: Governance-Rahmen für Kunden, die zwischen Investoren, Gesellschaften und Jurisdiktionen operieren.",
      chips: ["Gremien & Ausschüsse", "Entscheidungsrechte", "Reporting"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Was wir gestalten",
          items: [
            { title: "Gremienrahmen", desc: "Zusammensetzung, Kompetenzen und Rhythmus der Entscheidungsorgane — schriftlich und beschlossen." },
            { title: "Entscheidungsrechte", desc: "Wer was entscheidet, ab welcher Schwelle, mit welchem Quorum — ohne Grauzonen." },
            { title: "Aufsichtsreporting", desc: "Reporting, das zum Handeln gedacht ist, nicht zum Archiv." },
            { title: "Family Governance", desc: "Charten, Versammlungen und Eintrittsregeln für Familienvermögen." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "« Gute Governance macht Konflikte überflüssig — weil die Regeln vor den Meinungsverschiedenheiten existierten. »",
        },
      ],
    },
  },
  {
    slug: "standards/approche-reglementaire",
    fr: {
      eyebrow: "Standards · Approche réglementaire",
      title: "Une clarté réglementaire à travers les juridictions",
      intro:
        "Une lecture pratique de la conformité, de la gouvernance, des licences, du reporting et du choix de juridiction pour les clients internationaux.",
      chips: ["FINMA · Suisse", "UE", "Niveau international"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Trois principes",
          items: [
            { title: "Pratique, pas abstraite", desc: "Comment les exigences affectent la structure, les capitaux, le reporting et les délais — concrètement." },
            { title: "Coordination des conseils", desc: "Nous ne remplaçons pas vos avocats ou fiscalistes : nous cadrons les questions et traduisons leurs avis en décisions." },
            { title: "Conçu pour le changement", desc: "Bénéficiaires effectifs, reporting ESG, actifs numériques : nos engagements intègrent l'adaptabilité." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Points de vigilance systématiques",
          items: [
            { title: "Cartographie de juridiction", desc: "Comparer avant de choisir : fiscalité, stabilité, substance requise." },
            { title: "Questions de licence", desc: "Identifier tôt les activités réglementées d'un montage." },
            { title: "Obligations de reporting", desc: "Calendrier des déclarations par entité et par pays." },
            { title: "Bénéficiaires effectifs", desc: "Registres et transparence : anticiper plutôt que subir." },
            { title: "Alignement ESG", desc: "Intégrer les cadres de reporting durable sans surcharger la structure." },
            { title: "Coordination des conseils", desc: "Un seul interlocuteur pour orchestrer les spécialistes." },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Standards · Regulatory approach",
      title: "Regulatory clarity across jurisdictions",
      intro:
        "A practical view of compliance, governance, licensing, reporting and jurisdiction selection for international clients.",
      chips: ["FINMA · Switzerland", "EU", "International"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Three principles",
          items: [
            { title: "Practical, not abstract", desc: "How requirements affect structure, capital, reporting and timelines — concretely." },
            { title: "Counsel coordination", desc: "We do not replace your lawyers or tax advisers: we frame the questions and translate advice into decisions." },
            { title: "Built for change", desc: "Beneficial ownership, ESG reporting, digital assets: our engagements build in adaptability." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Systematic points of attention",
          items: [
            { title: "Jurisdiction mapping", desc: "Compare before choosing: taxation, stability, substance requirements." },
            { title: "Licensing questions", desc: "Identifying regulated activities in a structure early." },
            { title: "Reporting obligations", desc: "Filing calendar by entity and by country." },
            { title: "Beneficial ownership", desc: "Registers and transparency: anticipate rather than endure." },
            { title: "ESG alignment", desc: "Integrating sustainability reporting frameworks without overloading the structure." },
            { title: "Advisor coordination", desc: "One point of contact to orchestrate the specialists." },
          ],
        },
      ],
    },
    de: {
      eyebrow: "Standards · Regulatorischer Ansatz",
      title: "Regulatorische Klarheit über Jurisdiktionen hinweg",
      intro:
        "Eine praktische Sicht auf Compliance, Governance, Bewilligungen, Reporting und Jurisdiktionswahl für internationale Kunden.",
      chips: ["FINMA · Schweiz", "EU", "International"],
      sections: [
        {
          type: "numbered",
          theme: "light",
          title: "Drei Prinzipien",
          items: [
            { title: "Praktisch, nicht abstrakt", desc: "Wie Anforderungen Struktur, Kapital, Reporting und Zeitpläne konkret beeinflussen." },
            { title: "Beraterkoordination", desc: "Wir ersetzen Ihre Anwälte oder Steuerberater nicht: Wir rahmen die Fragen und übersetzen Gutachten in Entscheidungen." },
            { title: "Für Wandel gebaut", desc: "Wirtschaftlich Berechtigte, ESG-Reporting, digitale Assets: Unsere Mandate bauen Anpassungsfähigkeit ein." },
          ],
        },
        {
          type: "cards",
          theme: "dark",
          title: "Systematische Aufmerksamkeitspunkte",
          items: [
            { title: "Jurisdiktions-Mapping", desc: "Vergleichen vor der Wahl: Steuern, Stabilität, Substanzanforderungen." },
            { title: "Bewilligungsfragen", desc: "Regulierte Tätigkeiten einer Struktur früh identifizieren." },
            { title: "Meldepflichten", desc: "Meldekalender pro Gesellschaft und pro Land." },
            { title: "Wirtschaftlich Berechtigte", desc: "Register und Transparenz: antizipieren statt erleiden." },
            { title: "ESG-Ausrichtung", desc: "Nachhaltigkeits-Reporting-Rahmen integrieren, ohne die Struktur zu überlasten." },
            { title: "Beraterkoordination", desc: "Ein einziger Ansprechpartner, der die Spezialisten orchestriert." },
          ],
        },
      ],
    },
  },
  {
    slug: "standards/securite",
    fr: {
      eyebrow: "Standards · Sécurité",
      title: "La discrétion comme infrastructure",
      intro:
        "Protection des données, accès contrôlés et chaînes documentaires sécurisées : comment nous traitons les informations sensibles de nos clients.",
      chips: ["nLPD · RGPD", "Accès contrôlé", "Chiffrement"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Nos mesures",
          items: [
            { title: "Accès par rôle", desc: "Chaque dossier n'est visible que des personnes qui y travaillent." },
            { title: "Données protégées", desc: "Conformité nLPD suisse et RGPD européen, conservation minimale." },
            { title: "Échanges sécurisés", desc: "Documents sensibles transmis par des canaux chiffrés, jamais par simple email." },
            { title: "Traçabilité", desc: "Versions, validations et signatures documentées à chaque étape." },
          ],
        },
        {
          type: "text",
          theme: "dark",
          title: "Vos données ne servent qu'à vous répondre",
          paragraphs: [
            "Les informations transmises via ce site ne sont utilisées que pour traiter votre demande. Elles ne sont ni vendues, ni partagées, ni conservées au-delà du nécessaire.",
          ],
        },
      ],
    },
    en: {
      eyebrow: "Standards · Security",
      title: "Discretion as infrastructure",
      intro:
        "Data protection, controlled access and secure document chains: how we handle our clients' sensitive information.",
      chips: ["nFADP · GDPR", "Controlled access", "Encryption"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Our measures",
          items: [
            { title: "Role-based access", desc: "Each file is visible only to the people working on it." },
            { title: "Protected data", desc: "Swiss nFADP and European GDPR compliance, minimal retention." },
            { title: "Secure exchanges", desc: "Sensitive documents transmitted via encrypted channels, never plain email." },
            { title: "Traceability", desc: "Versions, validations and signatures documented at every step." },
          ],
        },
        {
          type: "text",
          theme: "dark",
          title: "Your data is only used to serve you",
          paragraphs: [
            "Information submitted through this site is used solely to process your request. It is never sold, shared or kept longer than necessary.",
          ],
        },
      ],
    },
    de: {
      eyebrow: "Standards · Sicherheit",
      title: "Diskretion als Infrastruktur",
      intro:
        "Datenschutz, kontrollierte Zugriffe und gesicherte Dokumentenketten: wie wir mit den sensiblen Informationen unserer Kunden umgehen.",
      chips: ["nDSG · DSGVO", "Kontrollierter Zugriff", "Verschlüsselung"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Unsere Massnahmen",
          items: [
            { title: "Rollenbasierter Zugriff", desc: "Jedes Dossier ist nur für die Personen sichtbar, die daran arbeiten." },
            { title: "Geschützte Daten", desc: "Konformität mit Schweizer nDSG und europäischer DSGVO, minimale Aufbewahrung." },
            { title: "Gesicherte Übermittlung", desc: "Sensible Dokumente werden über verschlüsselte Kanäle übermittelt, nie per einfacher E-Mail." },
            { title: "Nachverfolgbarkeit", desc: "Versionen, Validierungen und Unterschriften werden bei jedem Schritt dokumentiert." },
          ],
        },
        {
          type: "text",
          theme: "dark",
          title: "Ihre Daten dienen nur dazu, Ihnen zu antworten",
          paragraphs: [
            "Über diese Website übermittelte Informationen werden ausschliesslich zur Bearbeitung Ihrer Anfrage verwendet. Sie werden weder verkauft, weitergegeben noch länger als nötig aufbewahrt.",
          ],
        },
      ],
    },
  },
  {
    slug: "standards/durabilite",
    fr: {
      eyebrow: "Standards · Durabilité",
      title: "Un capital qui pense au-delà du cycle",
      intro:
        "Intégration ESG dans la structuration et la gestion d'actifs : performance durable, exigences de reporting et immobilier responsable.",
      chips: ["ESG intégré", "Immobilier responsable"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Nos engagements",
          items: [
            { title: "Sélection responsable", desc: "Performance énergétique et qualité d'usage intégrées aux critères d'analyse des actifs." },
            { title: "Reporting ESG", desc: "Indicateurs alignés sur les cadres en vigueur, sans bureaucratie superflue." },
            { title: "Valorisation durable", desc: "Rénovation et aménagement pensés pour le long terme, pas pour l'effet d'annonce." },
            { title: "Long terme", desc: "Des recommandations testées contre la durabilité, pas contre la mode." },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Standards · Sustainability",
      title: "Capital that thinks beyond the cycle",
      intro:
        "ESG integration in asset structuring and management: durable performance, reporting requirements and responsible real estate.",
      chips: ["Integrated ESG", "Responsible real estate"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Our commitments",
          items: [
            { title: "Responsible selection", desc: "Energy performance and quality of use built into asset analysis criteria." },
            { title: "ESG reporting", desc: "Indicators aligned with current frameworks, without needless bureaucracy." },
            { title: "Durable value creation", desc: "Renovation and fit-out designed for the long term, not for headlines." },
            { title: "Long horizon", desc: "Recommendations tested against durability, not fashion." },
          ],
        },
      ],
    },
    de: {
      eyebrow: "Standards · Nachhaltigkeit",
      title: "Kapital, das über den Zyklus hinausdenkt",
      intro:
        "ESG-Integration in Strukturierung und Asset Management: dauerhafte Performance, Reporting-Anforderungen und verantwortungsvolle Immobilien.",
      chips: ["Integriertes ESG", "Verantwortungsvolle Immobilien"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Unsere Verpflichtungen",
          items: [
            { title: "Verantwortungsvolle Auswahl", desc: "Energieeffizienz und Nutzungsqualität sind Teil der Analysekriterien." },
            { title: "ESG-Reporting", desc: "Kennzahlen nach geltenden Rahmenwerken, ohne überflüssige Bürokratie." },
            { title: "Dauerhafte Wertschöpfung", desc: "Renovation und Ausbau auf den langen Horizont ausgelegt, nicht auf Schlagzeilen." },
            { title: "Langer Horizont", desc: "Empfehlungen, geprüft auf Dauerhaftigkeit — nicht auf Moden." },
          ],
        },
      ],
    },
  },
  {
    slug: "standards/programme-partenaires",
    fr: {
      eyebrow: "Standards · Partenaires",
      title: "Un réseau de spécialistes, orchestré pour vous",
      intro:
        "Notaires, fiscalistes, banques, trustees et family offices : nous coordonnons un réseau de partenaires sélectionnés, au service d'un seul objectif — le vôtre.",
      chips: ["Réseau sélectionné", "Coordination centralisée"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Comment le réseau fonctionne",
          items: [
            { title: "Sélection rigoureuse", desc: "Chaque partenaire est évalué sur ses références, sa réactivité et sa compatibilité avec nos standards." },
            { title: "Indépendance préservée", desc: "Aucune rétrocession : nos recommandations de partenaires sont libres de tout intérêt caché." },
            { title: "Point de contact unique", desc: "Vous parlez à un conseiller ; nous orchestrons les spécialistes en arrière-plan." },
            { title: "Apporteurs d'affaires", desc: "Un cadre clair pour les partenaires qui nous adressent des mandats." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "« Nous choisissons nos partenaires comme nos structures : pour durer. »",
        },
      ],
    },
    en: {
      eyebrow: "Standards · Partner program",
      title: "A network of specialists, orchestrated for you",
      intro:
        "Notaries, tax advisers, banks, trustees and family offices: we coordinate a selected partner network in service of a single objective — yours.",
      chips: ["Selected network", "Centralised coordination"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "How the network works",
          items: [
            { title: "Rigorous selection", desc: "Every partner is assessed on references, responsiveness and compatibility with our standards." },
            { title: "Independence preserved", desc: "No retrocessions: our partner recommendations are free of hidden interests." },
            { title: "Single point of contact", desc: "You speak to one advisor; we orchestrate the specialists in the background." },
            { title: "Referral partners", desc: "A clear framework for partners who refer mandates to us." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "\u00AB We choose our partners like our structures: to last. \u00BB",
        },
      ],
    },
    de: {
      eyebrow: "Standards · Partnerprogramm",
      title: "Ein Netzwerk von Spezialisten, für Sie orchestriert",
      intro:
        "Notare, Steuerberater, Banken, Treuhänder und Family Offices: Wir koordinieren ein ausgewähltes Partnernetzwerk im Dienst eines einzigen Ziels — Ihres.",
      chips: ["Ausgewähltes Netzwerk", "Zentrale Koordination"],
      sections: [
        {
          type: "grid",
          theme: "light",
          title: "Wie das Netzwerk funktioniert",
          items: [
            { title: "Strenge Auswahl", desc: "Jeder Partner wird anhand von Referenzen, Reaktionsfähigkeit und Kompatibilität mit unseren Standards bewertet." },
            { title: "Bewahrte Unabhängigkeit", desc: "Keine Retrozessionen: Unsere Partnerempfehlungen sind frei von versteckten Interessen." },
            { title: "Einzige Anlaufstelle", desc: "Sie sprechen mit einem Berater; wir orchestrieren die Spezialisten im Hintergrund." },
            { title: "Vermittlungspartner", desc: "Ein klarer Rahmen für Partner, die uns Mandate vermitteln." },
          ],
        },
        {
          type: "quote",
          theme: "dark",
          text: "« Wir wählen unsere Partner wie unsere Strukturen: für die Dauer. »",
        },
      ],
    },
  },
];
